// Copyright ©️ 2026 eVolve MEP, LLC

/**
 * Elbow-style leader geometry for viewer tags, ported from the proof-of-concept
 * `LeaderGeometryHelper.js`. A leader is a diagonal segment from the element anchor to an elbow,
 * then a short screen-horizontal shoulder into the label attach point.
 */

import type { SelectedElementProperty } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/types';
import { enumFragmentVertices } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/geometryHelpers';
import { getDbIdWorldBounds, getFragmentList, getObjectTreeSync } from 'components/Autodesk/extensions/viewerHelpers';
import { THREE, toVector3, type Point3D, type ViewerCamera } from 'components/Autodesk/viewerThree';
import { isAlmostExactlyEqual } from 'helpers/isAlmostExactlyEqual';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { isInsignificant } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';

// region mesh updating
const LEADER_MIN_PIXEL_WIDTH = 2;

/** Scale a leader's world radius so it stays ≈`minPixelWidth` px wide on screen regardless of zoom. */
const computeScreenStableRadiusFt = (
  viewer: Autodesk.Viewing.GuiViewer3D,
  midpoint: THREE.Vector3,
  baseRadiusFt: number,
  minPixelWidth: number,
): number => {
  const camera = viewer.impl.camera as ViewerCamera;
  const height = viewer.container.clientHeight;
  if (!height || !Number.isFinite(minPixelWidth)) return baseRadiusFt;

  const distance = toVector3(camera.position).distanceTo(midpoint);
  let worldPerPixel: number;
  if (camera.isPerspectiveCamera) {
    const fov = ((camera.fov ?? 45) * Math.PI) / 180;
    worldPerPixel = (2 * Math.tan(fov / 2) * distance) / height;
  } else {
    const viewHeight = Math.abs((camera.top ?? 1) - (camera.bottom ?? -1)) || 1;
    worldPerPixel = viewHeight / height;
  }

  const screenRadius = (minPixelWidth / 2) * worldPerPixel;
  return Math.max(baseRadiusFt, screenRadius);
};

/**
 * Update `mesh`'s transform in place to represent a leader segment spanning `start`→`end`.
 *
 * `mesh` is expected to share a unit cylinder geometry (radius 1, height 1, axis along Y) across
 * all leader segments; this scales/positions/rotates that shared geometry rather than building a
 * new one, so segments never need their geometry recreated or disposed on every update.
 */
export const updateSegmentMesh = (
  mesh: THREE.Mesh,
  start: Point3D,
  end: Point3D,
  radiusFt: number,
  viewer: Autodesk.Viewing.GuiViewer3D,
  minPixelWidth: number = LEADER_MIN_PIXEL_WIDTH,
): void => {
  const startVec = toVector3(start);
  const endVec = toVector3(end);
  const direction = endVec.clone().sub(startVec);
  const length = direction.length();

  const midpoint = startVec.clone().add(endVec).multiplyScalar(0.5);
  const effectiveRadius = computeScreenStableRadiusFt(viewer, midpoint, radiusFt, minPixelWidth);

  mesh.position.copy(midpoint);
  mesh.scale.set(effectiveRadius, length, effectiveRadius);

  const axis = direction.normalize();
  const up = new THREE.Vector3(0, 1, 0);
  const quaternion = new THREE.Quaternion();
  if (isAlmostExactlyEqual(axis.dot(up), 1, 1e-3)) {
    quaternion.setFromAxisAngle(new THREE.Vector3(1, 0, 0), axis.y > 0 ? 0 : Math.PI);
  } else {
    quaternion.setFromUnitVectors(up, axis);
  }
  mesh.setRotationFromQuaternion(quaternion);
  // Meshes are made visible/invisible by directly mutating them
  // oxlint-disable-next-line evolve/no-param-reassign
  mesh.visible = true;
};

// endregion mesh updating

// region tag anchor/head geometry
const LEADER_MIN_SHOULDER_PX = 8;
const LEADER_DEGENERATE_PX = 4;

/**
 * The label's on-screen width in pixels.
 *
 * Declared here because it is a layout decision shared by two consumers: {@link TagLabel} renders the label at this
 * width, and leader layout needs the same number to find the label's left edge.
 */
export const TAG_LABEL_WIDTH_PX = 200;

/** The nearest point on the mesh to the world-space bounding-box center of an element, used as the default leader anchor. */
export const getElementAnchor = (model: Autodesk.Viewing.Model | null, dbId: number): THREE.Vector3 | null => {
  if (isNil(model)) return null;

  const tree = getObjectTreeSync(model);
  if (isNil(tree)) return null;

  const box = getDbIdWorldBounds(tree, dbId);
  if (box.isEmpty()) return null;
  const center = box.getCenter(new THREE.Vector3());

  // Find the nearest vertex on the mesh to the bounding-box center.
  const best = {
    point: new THREE.Vector3(Infinity, Infinity, Infinity),
    distance: Infinity,
  };
  const fragList = getFragmentList(model);
  tree.enumNodeFragments(
    dbId,
    (fragId) => {
      enumFragmentVertices(fragList, fragId, (vertex) => {
        const currentDist = vertex.distanceToSquared(center);
        if (currentDist < best.distance) {
          best.point.copy(vertex);
          best.distance = currentDist;
        }
      });
    },
    true,
  );
  // If we found no valid nearest vertex, fall back to the bounding-box center.
  return Number.isFinite(best.distance) ? best.point : center;
};

/** A default label head offset to the camera's right of the anchor, so the label clears the element. */
export const computeDefaultHead = (
  viewer: Autodesk.Viewing.GuiViewer3D,
  anchor: Point3D | null,
  offsetFt: number,
): THREE.Vector3 | null => {
  if (isNil(anchor)) return null;

  const camera = viewer.impl.camera as ViewerCamera;
  const viewDir = new THREE.Vector3();
  camera.getWorldDirection(viewDir);

  const up = camera.up.clone().normalize();
  const right = new THREE.Vector3().crossVectors(viewDir, up);
  if (isInsignificant(right.lengthSq())) {
    right.set(1, 0, 0);
  } else {
    right.normalize();
  }

  return toVector3(anchor).add(right.multiplyScalar(offsetFt));
};

// endregion tag anchor/head geometry

// region tag coordinates
/**
 * Screen ⇄ world coordinate conversions for viewer tags, ported from the proof-of-concept
 * `TagDragHelper.js` / `LeaderGeometryHelper.js`. All THREE usage is the viewer's bundled build.
 */

/** A point projected into the viewer container's pixel space, with a frustum-visibility flag. */
export type ContainerPoint = {
  x: number;
  y: number;
  visible: boolean;
};
/** Project a world-space point into the viewer container's pixel coordinates. */
export const projectWorldToContainer = (viewer: Autodesk.Viewing.GuiViewer3D, worldPoint: Point3D): ContainerPoint => {
  const camera = viewer.impl.camera;
  const { clientWidth: width, clientHeight: height } = viewer.container;
  const projected = toVector3(worldPoint).project(camera);

  return {
    x: ((projected.x + 1) * width) / 2,
    y: ((-projected.y + 1) * height) / 2,
    visible: projected.z >= -1 && projected.z <= 1 && Math.abs(projected.x) <= 1.2 && Math.abs(projected.y) <= 1.2,
  };
};
/** Convert a client (page) coordinate into the viewer container's pixel space. */
export const screenToContainerCoords = (viewer: Autodesk.Viewing.GuiViewer3D, clientX: number, clientY: number) => {
  const rect = viewer.container.getBoundingClientRect();
  return { x: clientX - rect.left, y: clientY - rect.top };
};
/**
 * Unproject a container pixel coordinate onto the camera plane that passes through `planePoint`,
 * returning the world-space intersection. Used to keep a dragged label on a stable depth.
 */
export const containerPointToWorldOnCameraPlane = (
  viewer: Autodesk.Viewing.GuiViewer3D,
  containerX: number,
  containerY: number,
  planePoint: Point3D,
): THREE.Vector3 => {
  const { clientWidth: width, clientHeight: height } = viewer.container;

  if (!width || !height || !Number.isFinite(containerX) || !Number.isFinite(containerY)) {
    return toVector3(planePoint);
  }

  const camera = viewer.impl.camera;
  const ndcX = (containerX / width) * 2 - 1;
  const ndcY = -(containerY / height) * 2 + 1;
  const nearPoint = new THREE.Vector3(ndcX, ndcY, -1).unproject(camera);
  const farPoint = new THREE.Vector3(ndcX, ndcY, 1).unproject(camera);
  const direction = farPoint.sub(nearPoint).normalize();

  const planeRef = toVector3(planePoint);
  const normal = new THREE.Vector3();
  (camera as ViewerCamera).getWorldDirection(normal);

  const denom = normal.dot(direction);
  if (isInsignificant(denom)) return planeRef;

  const distance = planeRef.clone().sub(nearPoint).dot(normal) / denom;
  if (distance < 0) return planeRef;

  return nearPoint.clone().add(direction.clone().multiplyScalar(distance));
};

/** Convert a client (page) coordinate onto the camera plane through `planePoint` in world space. */
export const clientToWorldOnCameraPlane = (
  viewer: Autodesk.Viewing.GuiViewer3D,
  clientX: number,
  clientY: number,
  planePoint: Point3D,
): THREE.Vector3 => {
  const coords = screenToContainerCoords(viewer, clientX, clientY);
  return containerPointToWorldOnCameraPlane(viewer, coords.x, coords.y, planePoint);
};

/**
 * The container point the leader attaches to on the label (its left-middle edge).
 * Derived from the projected `head` point, offset by half the label width to account for the label
 * having a `transform: translate(-50%, -50%)` style.
 */
const getLabelAttachContainerPoint = (viewer: Autodesk.Viewing.GuiViewer3D, head: Point3D): ContainerPoint => {
  const headPx = projectWorldToContainer(viewer, head);
  return { ...headPx, x: headPx.x - TAG_LABEL_WIDTH_PX / 2 };
};
// endregion tag coordinates

// region leader line geometry
/** Generates the leader line layout. */
export const computeSegments = (
  viewer: Autodesk.Viewing.GuiViewer3D,
  anchor: Point3D,
  head: Point3D,
  shoulderLengthPx: number,
) => {
  const vector3anchor = toVector3(anchor);
  const anchorPx = projectWorldToContainer(viewer, anchor);
  const attachPx = getLabelAttachContainerPoint(viewer, head);

  const shoulderPx = Math.max(shoulderLengthPx, LEADER_MIN_SHOULDER_PX);
  const shoulderSign = attachPx.x - anchorPx.x >= 0 ? -1 : 1;
  const elbowPx = { x: attachPx.x + shoulderSign * shoulderPx, y: attachPx.y };

  const shoulderScreenLength = Math.abs(elbowPx.x - attachPx.x);
  const diagonalScreenLength = Math.hypot(elbowPx.x - anchorPx.x, elbowPx.y - anchorPx.y);

  const planePoint = toVector3(head);
  const attach = containerPointToWorldOnCameraPlane(viewer, attachPx.x, attachPx.y, planePoint);

  if (shoulderScreenLength < LEADER_DEGENERATE_PX || diagonalScreenLength < LEADER_DEGENERATE_PX) {
    return [{ start: vector3anchor.clone(), end: attach.clone() }];
  }

  const elbow = containerPointToWorldOnCameraPlane(viewer, elbowPx.x, elbowPx.y, planePoint);
  return [
    { start: vector3anchor.clone(), end: elbow.clone() },
    { start: elbow.clone(), end: attach.clone() },
  ];
};

/**
 * Update a tag's elbow leader meshes in place for its current layout.
 *
 * `meshes` is always the same fixed pair for a given tag (a single-segment leader still has 2
 * mesh slots — the second is simply hidden). This never creates or destroys geometry; it only
 * repositions/rescales/rotates the existing meshes (and toggles `visible` on any unused slot).
 */
export const updateElbowLeaderMeshes = (
  viewer: Autodesk.Viewing.GuiViewer3D,
  anchor: Point3D,
  head: Point3D,
  meshes: readonly [THREE.Mesh, THREE.Mesh],
  radiusFt: number,
  shoulderLengthPx: number,
  minPixelWidth: number = LEADER_MIN_PIXEL_WIDTH,
): void => {
  const segments = computeSegments(viewer, anchor, head, shoulderLengthPx);
  meshes.forEach((mesh, index) => {
    const segment = segments[index];
    if (!segment) {
      // Meshes are made visible/invisible by directly mutating them
      // oxlint-disable-next-line evolve/no-param-reassign
      mesh.visible = false;
      return;
    }
    updateSegmentMesh(mesh, segment.start, segment.end, radiusFt, viewer, minPixelWidth);
  });
};
// endregion leader line geometry

// region property display order
/**
 * Properties in this list are shown first, in this order, ahead of the remaining properties
 * (which otherwise stay in their default alphabetical order).
 */
const PRIORITY_PROPERTY_ORDER = new Map(
  [
    'eVolve_Description',
    'eVolve_DescriptionTwo',
    'eVolve_Length',
    'Length',
    'Size',
    'Product Long Description',
    'OEM',
    'Fabrication Service',
  ].map((name, index) => [name, index]),
);

/** Sorts the list of properties, first by priority order, then alphabetically. */
export const sortPropertiesComp = (a: SelectedElementProperty['name'], b: SelectedElementProperty['name']) => {
  const aPriority = PRIORITY_PROPERTY_ORDER.get(a);
  const bPriority = PRIORITY_PROPERTY_ORDER.get(b);
  if (isNotNil(aPriority) && isNotNil(bPriority)) return aPriority - bPriority;
  if (isNotNil(aPriority)) return -1;
  if (isNotNil(bPriority)) return 1;
  return a.localeCompare(b, undefined);
};
// endregion property display order
